import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TrendingUp,
  Plus,
  AlertCircle,
  AlertTriangle,
  Trash2,
  X,
  Calendar,
  Store,
  Tag,
} from 'lucide-react'
import { getLatestPrices, getPriceHistory, recordBulkPrices, deletePrice } from '../../services/api'
import { format, parseISO } from 'date-fns'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'

const ANIMAL_TYPES = ['bull', 'cow', 'steer', 'heifer', 'calf']

const TYPE_CONFIG = {
  bull:   { label: 'Bull',   color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  cow:    { label: 'Cow',    color: 'text-primary-700',  bg: 'bg-primary-50',  border: 'border-primary-200' },
  steer:  { label: 'Steer',  color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200' },
  heifer: { label: 'Heifer', color: 'text-purple-700',  bg: 'bg-purple-50',  border: 'border-purple-200' },
  calf:   { label: 'Calf',   color: 'text-pink-700',    bg: 'bg-pink-50',    border: 'border-pink-200' },
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-UG', {
    style: 'currency', currency: 'UGX',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount)

const getAgeBadge = (days) => {
  if (days <= 7) return { color: 'bg-green-100 text-green-700', label: 'Current' }
  if (days <= 30) return { color: 'bg-green-50 text-green-600', label: `${days}d ago` }
  if (days <= 60) return { color: 'bg-amber-100 text-amber-700', label: `${days}d ago` }
  return { color: 'bg-red-100 text-red-700', label: `${days}d ago` }
}

export default function MarketPrices({ ranchId }) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: latest, isLoading: latestLoading } = useQuery({
    queryKey: ['latestPrices', ranchId],
    queryFn: () => getLatestPrices(ranchId),
  })

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['priceHistory', ranchId, typeFilter],
    queryFn: () => getPriceHistory(ranchId, typeFilter ? { animal_type: typeFilter } : {}),
  })

  const deleteMutation = useMutation({
    mutationFn: deletePrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['latestPrices'] })
      queryClient.invalidateQueries({ queryKey: ['priceHistory'] })
      setDeleteConfirm(null)
    },
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Market Prices</h1>
          <p className="text-gray-500 mt-1">Track livestock prices for herd valuation</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Record Prices
        </button>
      </div>

      {/* Staleness Alert */}
      {latest?.is_stale && (
        <div className={clsx(
          'flex items-center gap-3 p-4 rounded-lg border',
          latest.staleness_severity === 'red'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        )}>
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <span className="font-medium">Prices are {latest.max_age_days} days old</span>
            {' — '}visit the market and record updated prices to keep herd valuations accurate.
          </div>
        </div>
      )}

      {/* Latest Prices Grid */}
      <div>
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Latest Prices</h2>
        {latestLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !latest?.prices?.length ? (
          <div className="card p-8 text-center">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No prices recorded</h3>
            <p className="text-gray-500 mt-1">Record your first market prices to get started</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn bg-green-600 text-white hover:bg-green-700 mt-4 inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Record Prices
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {latest.prices.map((p) => {
              const cfg = TYPE_CONFIG[p.animal_type] || TYPE_CONFIG.cow
              const badge = getAgeBadge(p.age_days)
              return (
                <div key={p.id} className={clsx('card p-5 border-t-4', cfg.border)}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={clsx('text-sm font-semibold', cfg.color)}>
                      {cfg.label}
                    </span>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', badge.color)}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(p.price_per_kg)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">per kg liveweight</div>
                  {p.market_name && (
                    <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Store className="h-3 w-3" />
                      {p.market_name}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Price History */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Price History</h2>
          </div>
          {/* Type filter tabs */}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              onClick={() => setTypeFilter('')}
              className={clsx(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                !typeFilter ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              All
            </button>
            {ANIMAL_TYPES.map(t => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                  typeFilter === t ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {TYPE_CONFIG[t]?.label || t}
              </button>
            ))}
          </div>
        </div>

        {historyLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !history?.length ? (
          <div className="text-center py-12">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No price history{typeFilter ? ` for ${TYPE_CONFIG[typeFilter]?.label}` : ''}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price/kg</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((r) => {
                  const cfg = TYPE_CONFIG[r.animal_type] || TYPE_CONFIG.cow
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(parseISO(r.observation_date), 'dd MMM yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', cfg.bg, cfg.color)}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(r.price_per_kg)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.market_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.source || 'manual'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {deleteConfirm === r.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => deleteMutation.mutate(r.id)}
                              className="text-red-600 text-xs font-medium hover:text-red-800"
                              disabled={deleteMutation.isPending}
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-gray-400 text-xs hover:text-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(r.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record Prices Modal */}
      {showModal && (
        <RecordPriceModal
          ranchId={ranchId}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['latestPrices'] })
            queryClient.invalidateQueries({ queryKey: ['priceHistory'] })
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}

function RecordPriceModal({ ranchId, onClose, onSuccess }) {
  const { userId } = useAuth()
  const today = new Date().toISOString().split('T')[0]
  const [observationDate, setObservationDate] = useState(today)
  const [marketName, setMarketName] = useState('')
  const [source, setSource] = useState('manual')
  const [prices, setPrices] = useState({})
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: recordBulkPrices,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record prices'),
  })

  const toggleType = (type) => {
    setPrices(prev => {
      const copy = { ...prev }
      if (copy[type] !== undefined) {
        delete copy[type]
      } else {
        copy[type] = ''
      }
      return copy
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    const priceEntries = Object.entries(prices)
      .filter(([, val]) => val && parseFloat(val) > 0)
      .map(([animal_type, price_per_kg]) => ({
        animal_type,
        price_per_kg: parseFloat(price_per_kg),
      }))

    if (priceEntries.length === 0) {
      setError('Enter at least one price')
      return
    }

    mutation.mutate({
      ranch_id: ranchId,
      observation_date: observationDate,
      market_name: marketName || null,
      source,
      recorded_by: userId,
      prices: priceEntries,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Record Market Prices</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Date & Market */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Observation Date *</label>
              <input
                type="date"
                value={observationDate}
                onChange={e => setObservationDate(e.target.value)}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="label">Market Name</label>
              <input
                type="text"
                value={marketName}
                onChange={e => setMarketName(e.target.value)}
                placeholder="e.g. Soroti Market"
                className="input w-full"
              />
            </div>
          </div>

          {/* Source */}
          <div>
            <label className="label">Source</label>
            <select
              value={source}
              onChange={e => setSource(e.target.value)}
              className="input w-full"
            >
              <option value="manual">Manual observation</option>
              <option value="phone_call">Phone call</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="broker">Broker/agent</option>
            </select>
          </div>

          {/* Animal Type Selection */}
          <div>
            <label className="label">Select Animal Types & Prices *</label>
            <p className="text-xs text-gray-400 mb-2">Tap a type to add it, then enter the price per kg</p>
            <div className="space-y-3">
              {ANIMAL_TYPES.map(type => {
                const cfg = TYPE_CONFIG[type]
                const isSelected = prices[type] !== undefined
                return (
                  <div key={type} className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleType(type)}
                      className={clsx(
                        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border min-w-[100px]',
                        isSelected
                          ? `${cfg.bg} ${cfg.color} ${cfg.border}`
                          : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                      )}
                    >
                      <Tag className="h-3.5 w-3.5" />
                      {cfg.label}
                    </button>
                    {isSelected && (
                      <div className="flex-1 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">UGX</span>
                        <input
                          type="number"
                          value={prices[type]}
                          onChange={e => setPrices(prev => ({ ...prev, [type]: e.target.value }))}
                          placeholder="Price per kg"
                          className="input w-full pl-12"
                          min="0"
                          step="100"
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  Save Prices
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
